import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useAuth } from '../contexts/AuthContext';
import ProfessionalHeader from '../components/ProfessionalHeader';
import QuickFitPlayerCard from '../components/QuickFitPlayerCard';
import MLRecommendationResults from '../components/MLRecommendationResults';
import WedgeRecommendationResults from '../components/WedgeRecommendationResults';
import {
  QuickFitPlayerForm,
  QuickFitClubOptions,
  QuickFitPlayerResult,
  QuickFitModelMode,
  getQuickFitOptions,
  submitQuickFit,
  createEmptyPlayer,
} from '../services/quickFitService';
import { logEvent, newSessionId } from '../services/telemetryService';

const MAX_PLAYERS = 5;

function validatePlayer(p: QuickFitPlayerForm): string | null {
  if (!p.handicap || isNaN(parseInt(p.handicap, 10))) return `${p.name}: Handicap is required`;
  if (!p.age || isNaN(parseInt(p.age, 10))) return `${p.name}: Age is required`;
  if (!p.swing_speed_driver || isNaN(parseInt(p.swing_speed_driver, 10)))
    return `${p.name}: Driver swing speed is required`;
  if (!p.swing_speed_7iron || isNaN(parseInt(p.swing_speed_7iron, 10)))
    return `${p.name}: 7-iron swing speed is required`;
  return null;
}

const QuickFitPage: React.FC = () => {
  const { user, logout } = useAuth();

  const [clubOptions, setClubOptions] = useState<QuickFitClubOptions>({ drivers: [], irons: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);

  const [players, setPlayers] = useState<QuickFitPlayerForm[]>([
    createEmptyPlayer(Date.now().toString(), 0),
  ]);

  const [results, setResults] = useState<QuickFitPlayerResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelMode, setModelMode] = useState<QuickFitModelMode>('v9');

  // Telemetry
  const sessionIdRef = useRef<string>(newSessionId());
  const startTimeRef = useRef<number | null>(null);

  // Per-player results tabs
  const [activePlayerTab, setActivePlayerTab] = useState(0);
  const [activeEquipmentTab, setActiveEquipmentTab] = useState<'driver' | 'irons' | 'wedges'>('driver');

  useEffect(() => {
    getQuickFitOptions()
      .then(setClubOptions)
      .catch(() => {})
      .finally(() => setOptionsLoading(false));

    // Telemetry: detect return visit (prefilled) vs first visit
    const VISIT_KEY = 'quick_fit_visited';
    const isReturn = sessionStorage.getItem(VISIT_KEY) === 'true';
    sessionStorage.setItem(VISIT_KEY, 'true');
    startTimeRef.current = Date.now();

    logEvent({
      session_id: sessionIdRef.current,
      event_type: isReturn ? 'quick_fit_prefilled' : 'quick_fit_started',
      metadata: { player_count: 1 },
    });

    // Abandon: fires when user navigates away before completing
    const sessionId = sessionIdRef.current;
    const startTime = startTimeRef;
    return () => {
      if (startTime.current) {
        const duration = (Date.now() - startTime.current) / 1000;
        // Only log abandon if they spent >5s (filters out accidental visits)
        if (duration > 5) {
          logEvent({
            session_id: sessionId,
            event_type: 'quick_fit_abandoned',
            duration_seconds: duration,
          });
        }
      }
    };
  }, []);

  const addPlayer = useCallback(() => {
    if (players.length >= MAX_PLAYERS) return;
    setPlayers(prev => [...prev, createEmptyPlayer(Date.now().toString(), prev.length)]);
  }, [players.length]);

  const removePlayer = useCallback((index: number) => {
    setPlayers(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updatePlayer = useCallback((index: number, updated: QuickFitPlayerForm) => {
    setPlayers(prev => prev.map((p, i) => (i === index ? updated : p)));
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);
    for (const p of players) {
      const err = validatePlayer(p);
      if (err) { setError(err); return; }
    }

    setLoading(true);
    try {
      const allResults = await submitQuickFit(players, modelMode);
      setResults(allResults);
      setActivePlayerTab(0);
      setActiveEquipmentTab('driver');

      const duration = startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : undefined;
      const firstResult = allResults[0];
      logEvent({
        session_id: sessionIdRef.current,
        event_type: 'quick_fit_completed',
        duration_seconds: duration,
        metadata: {
          player_count: players.length,
          model_mode: modelMode,
          driver_scoring_version: firstResult?.driverResults?.metadata?.scoring_version ?? null,
          iron_scoring_version: firstResult?.ironResults?.metadata?.scoring_version ?? null,
          top_driver: firstResult?.driverResults?.heads?.[0]
            ? `${firstResult.driverResults.heads[0].head_brand} ${firstResult.driverResults.heads[0].head_model}`
            : null,
        },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to get recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [players, modelMode]);

  const handleBackToForm = useCallback(() => {
    setResults(null);
    setError(null);
    // Reset session for the next fitting attempt
    sessionIdRef.current = newSessionId();
    startTimeRef.current = Date.now();
  }, []);

  const activeResult = results?.[activePlayerTab] ?? null;

  const equipmentTabs = [
    { key: 'driver' as const, label: 'Driver' },
    { key: 'irons' as const, label: 'Irons' },
    { key: 'wedges' as const, label: 'Wedges' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfessionalHeader user={user} onLogout={logout} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Quick Fit Multiple</h2>
          <p className="text-gray-500 mt-1">
            Add up to {MAX_PLAYERS} players, fill in their details, and get driver + iron + wedge
            recommendations for everyone at once.
          </p>
        </div>

        {results ? (
          /* ── Results ─────────────────────────────────────────────────── */
          <div>
            <button
              onClick={handleBackToForm}
              className="mb-6 text-green-ink hover:text-green-ink font-medium flex items-center text-sm"
            >
              ← Back to players
            </button>

            {/* Player tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {results.map((r, i) => (
                <button
                  key={i}
                  onClick={() => { setActivePlayerTab(i); setActiveEquipmentTab('driver'); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activePlayerTab === i
                      ? 'bg-green-ink text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-green-ink'
                  }`}
                >
                  {r.playerName}
                  {r.error && <span className="ml-1 text-red-300">!</span>}
                </button>
              ))}
            </div>

            {activeResult?.error ? (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <p className="text-red-700 text-sm">
                  Error for {activeResult.playerName}: {activeResult.error}
                </p>
              </div>
            ) : (
              <>
                {/* Equipment tabs */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="flex gap-6">
                    {equipmentTabs.map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveEquipmentTab(tab.key)}
                        className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                          activeEquipmentTab === tab.key
                            ? 'border-green-ink text-green-ink'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {activeEquipmentTab === 'driver' && (
                  <MLRecommendationResults
                    data={activeResult?.driverResults ?? null}
                    loading={false}
                    error={activeResult?.driverResults ? null : 'No driver recommendations available'}
                    onBack={handleBackToForm}
                    sessionId={sessionIdRef.current}
                  />
                )}

                {activeEquipmentTab === 'irons' && (
                  activeResult?.ironResults ? (
                    <MLRecommendationResults
                      data={activeResult.ironResults}
                      loading={false}
                      error={null}
                      onBack={handleBackToForm}
                      sessionId={sessionIdRef.current}
                    />
                  ) : (
                    <div className="text-center py-12 text-gray-500">Iron recommendations not available.</div>
                  )
                )}

                {activeEquipmentTab === 'wedges' && (
                  <WedgeRecommendationResults
                    data={activeResult?.wedgeResults ?? null}
                    loading={false}
                    error={activeResult?.wedgeResults ? null : 'No wedge recommendations available'}
                    onBack={handleBackToForm}
                  />
                )}
              </>
            )}
          </div>
        ) : (
          /* ── Form ────────────────────────────────────────────────────── */
          <div>
            {optionsLoading && (
              <div className="text-center py-4 text-gray-400 text-sm">Loading club options...</div>
            )}

            <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">Recommendation Engine</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    V9 uses the latest Physics + AI constraint-first driver and iron endpoints.
                    Wedges remain rule-based.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setModelMode('v9')}
                    className={`px-4 py-2 rounded-md text-sm font-medium border ${
                      modelMode === 'v9'
                        ? 'bg-green-ink text-white border-green-ink'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink'
                    }`}
                  >
                    V9 Physics + AI
                  </button>
                  <button
                    type="button"
                    onClick={() => setModelMode('rule_based')}
                    className={`px-4 py-2 rounded-md text-sm font-medium border ${
                      modelMode === 'rule_based'
                        ? 'bg-green-ink text-white border-green-ink'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-green-ink'
                    }`}
                  >
                    Legacy Rule-Based
                  </button>
                </div>
              </div>
            </div>

            {/* Player cards */}
            {players.map((player, i) => (
              <QuickFitPlayerCard
                key={player.id}
                player={player}
                index={i}
                clubOptions={clubOptions}
                onChange={updated => updatePlayer(i, updated)}
                onRemove={() => removePlayer(i)}
                canRemove={players.length > 1}
              />
            ))}

            {/* Add player button */}
            {players.length < MAX_PLAYERS && (
              <button
                onClick={addPlayer}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-green-ink hover:text-green-ink transition-colors mb-6"
              >
                + Add Player ({players.length}/{MAX_PLAYERS})
              </button>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 rounded">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={loading || optionsLoading}
              className="w-full py-3 bg-green-ink hover:opacity-90 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? `Getting recommendations for ${players.length} player${players.length > 1 ? 's' : ''}...`
                : `Get ${modelMode === 'v9' ? 'V9' : 'Rule-Based'} Recommendations for ${players.length > 1 ? `All ${players.length} Players` : '1 Player'}`}
            </button>

            <p className="text-center text-xs text-gray-400 mt-3">
              Required: Handicap, Age, Driver swing speed, 7-iron swing speed. Add 3+ V9 LM fields per club for non-fallback archetype testing.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default QuickFitPage;
